import { useCallback, useEffect, useMemo, useState } from 'react';
import { Menu, Flex } from 'antd';
import { Dropdown } from '@/components/AntdCompatDropdown';
import { ChevronDownIcon } from '@/assets/icons/icons';
import { CustomButton } from '@/components';
import type { CalendarApi, ViewApi } from '@fullcalendar/core';
import type { CalendarViewBy } from '../types';
import ColorLegends from './calendar/ColorLegends';
import type { CalendarLegendLegType } from './calendar/ColorLegends';
import { useBreakpoints } from '@/hooks/useBreakpoints';
import useUserDateTimeFormat from '@/hooks/useUserDateTimeFormat';

type CalendarToolbarProps = {
  tableView?: boolean;
  calendarApi?: CalendarApi;
  viewBy: CalendarViewBy;
  setViewBy: (v: CalendarViewBy) => void;
  onTodayOpenMore?: () => void;
  legType?: CalendarLegendLegType;
};

type ViewByOption = { label: string; value: CalendarViewBy };
type ViewOption = { value: string; label: string };

const CalendarToolbar = ({
  tableView = false,
  calendarApi,
  viewBy,
  onTodayOpenMore,
  legType = 'CL',
  setViewBy,
}: CalendarToolbarProps) => {
  const { xs, sm } = useBreakpoints();
  const { dateFormat } = useUserDateTimeFormat();

  const formatUnifiedTitle = useCallback(
    (view: ViewApi) => {
      const start = view.currentStart; // the actual date range start
      const month = start.toLocaleDateString('en-US', { month: 'long' });
      const year = start.getFullYear();
      // Year-first preference (YYYY/MM/DD) shows "2026 August" instead of
      // "August 2026"; month/day-first preferences don't affect this
      // month+year-only title since there's no day component to reorder.
      return dateFormat?.startsWith('YYYY')
        ? `${year} ${month}`
        : `${month} ${year}`;
    },
    [dateFormat]
  );

  const [currentTitle, setCurrentTitle] = useState('');

  // const options: ViewOption[] = useMemo(
  //   () => [
  //     { value: 'timeGridWeek', label: 'Week' },
  //     { value: 'dayGridMonth', label: 'Month' },
  //   ],
  //   []
  // );

  const options: ViewOption[] = useMemo(() => {
    const isMobile = xs || sm;
    return [
      ...(tableView && isMobile
        ? [
            //  { value: 'timeGridDay', label: 'Day' },
            { value: 'timeGridThreeDay', label: '3 Days' },
          ]
        : []),
      { value: 'timeGridWeek', label: 'Week' },
      { value: 'dayGridMonth', label: 'Month' },
    ];
  }, [tableView, xs, sm]);

  const viewByOptions: ViewByOption[] = useMemo(
    () => [
      { value: 'pickup_date', label: 'Pickup date' },
      { value: 'created_at', label: 'Created date' },
      {
        value: 'arrival',
        label: `Arrival ${tableView && (xs || sm) ? 'date' : '(Delivered or ETA)'}`,
      },
    ],
    [sm, xs, tableView]
  );

  const [viewBySelected, setViewBySelected] = useState<ViewByOption | null>(
    viewByOptions.find((opt) => opt.value === viewBy) || null
  );

  useEffect(() => {
    if (!calendarApi) return;
    setCurrentTitle(formatUnifiedTitle(calendarApi!.view));
  }, [calendarApi, formatUnifiedTitle]);

  useEffect(() => {
    setViewBySelected(
      viewByOptions.find((opt) => opt.value === viewBy) || null
    );
  }, [viewBy, viewByOptions]);

  useEffect(() => {
    if (!calendarApi) return;
    setCurrentTitle(formatUnifiedTitle(calendarApi.view));
  }, [calendarApi, formatUnifiedTitle]);

  const handlePrev = () => {
    calendarApi?.prev();
    setCurrentTitle(formatUnifiedTitle(calendarApi!.view));
  };

  const handleNext = () => {
    calendarApi?.next();
    setCurrentTitle(formatUnifiedTitle(calendarApi!.view));
  };

  const handleToday = () => {
    calendarApi?.today();
    setCurrentTitle(formatUnifiedTitle(calendarApi!.view));
    onTodayOpenMore?.();
  };

  const handleViewChange = (view: string) => {
    calendarApi?.changeView(view);
    setCurrentTitle(formatUnifiedTitle(calendarApi!.view));
  };

  // Initialize title when component mounts
  useEffect(() => {
    if (calendarApi) {
      setCurrentTitle(formatUnifiedTitle(calendarApi!.view));
    }
  }, [calendarApi, formatUnifiedTitle]);

  const [selected, setSelected] = useState<ViewOption>(options[0]);

  useEffect(() => {
    if (!calendarApi) return;

    const type = calendarApi.view.type;
    const next = options.find((o) => o.value === type) ?? options[0];
    setSelected(next);
  }, [calendarApi, options, currentTitle]);

  const menu = (
    <Menu>
      {options.map((opt) => (
        <Menu.Item
          key={opt.value}
          onClick={() => {
            setSelected(opt);
            handleViewChange(opt.value);
          }}
        >
          {opt.label}
        </Menu.Item>
      ))}
    </Menu>
  );

  const viewByMenu = (
    <Menu>
      {viewByOptions.map((opt) => (
        <Menu.Item
          key={opt.value}
          onClick={() => {
            setViewBySelected(opt);
            setViewBy(opt.value);
          }}
        >
          {opt.label}
        </Menu.Item>
      ))}
    </Menu>
  );

  return (
    <Flex
      justify='space-between'
      align='center'
      gap={8}
      wrap
      className={`calendar-toolbar-wrapper ${tableView && 'is-table-view'} `}
    >
      <Flex
        className={`responsive-gap-16 ${tableView && 'is-table-view'}`}
        wrap={!tableView}
        align='center'
      >
        {/* desktop  */}
        <Flex gap={16} align='center' className={tableView && 'sm-d-none'}>
          <span className='zl-heading-18'>{currentTitle}</span>

          <Flex gap={20} align='center' className='cursor-pointer'>
            <span onClick={handlePrev}>‹</span>
            <span onClick={handleNext}>›</span>
          </Flex>
        </Flex>
        {/* responsive for mobile  */}
        {tableView && (
          <Flex
            align='center'
            className='sm-d-inline-flex cursor-pointer responsive-gap-10 is-table-view'
          >
            <span onClick={handlePrev}>‹</span>
            <span className='w-fit calender-month-font'>
              {dateFormat?.startsWith('YYYY')
                ? currentTitle.split(' ')[1]
                : currentTitle.split(' ')[0]}
            </span>
            <span onClick={handleNext}>›</span>
          </Flex>
        )}

        <CustomButton
          onClick={handleToday}
          text='Today'
          bgColor='transparent'
          color='black'
          lineHeight='16px'
          boxShadow='none'
          borderRadius='24px'
          border='1px solid #000'
          className={`dropdown-btn ${tableView && 'is-today-btn'}`}
        />

        <Flex
          className={` ${tableView && 'sm-d-none'}`}
          gap={8}
          align='center'
          wrap
        >
          <ColorLegends legType={legType} />
        </Flex>
      </Flex>

      <Flex gap={8} align='center'>
        <Dropdown overlay={menu} trigger={['click']}>
          <Flex
            gap={4}
            align='center'
            className={`cursor-pointer dropdown-btn ${tableView && 'dropdown-btn-table-view'} `}
          >
            <p>{selected.label}</p>
            <ChevronDownIcon className='w-3 h-3' />
          </Flex>
        </Dropdown>

        <Dropdown overlay={viewByMenu} trigger={['click']}>
          <Flex
            gap={4}
            align='center'
            className={`cursor-pointer dropdown-btn ${tableView && 'dropdown-btn-table-view'} `}
          >
            <p>
              {' '}
              <span className='sm-d-none'>View by</span> {viewBySelected?.label}
            </p>
            <ChevronDownIcon className='w-3 h-3' />
          </Flex>
        </Dropdown>
      </Flex>
    </Flex>
  );
};

export default CalendarToolbar;
